/**
 * @flow
 */

import React from 'react';
import styled from 'styled-components';
import { Controller, useFormContext } from 'react-hook-form';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import { format, parse, isValid } from 'date-fns';
import { Input, TextArea, Form, Label as BaseLabel } from 'semantic-ui-react';
import { FormLabel } from '../../../../components';
import BaseCalendar from '../../../../containers/Calendar';
import SeminarType from './SeminarType';
import SeminarTime from './SeminarTime';
import SeminarDuration from './SeminarDuration';
import BannerPreviewLink from './BannerPreviewLink';
import styles from '../../../../styles';

const Label = styled(FormLabel)`
    ${styles.proximaNovaFontFamily} !important;
`;

const Calendar = styled(BaseCalendar)`
    & .DayPickerInput-OverlayWrapper {
        top: 46px;
    }
`;

const intlMessages = defineMessages({
    datePlaceholder: {
        id: 'pages.seminars.form.SeminarForm.date.placeholder',
        defaultMessage: 'DD.MM.YYYY',
    },
    timePlaceholder: {
        id: 'pages.seminars.form.SeminarForm.time.placeholder',
        defaultMessage: 'HH:MM',
    },
    durationPlaceholder: {
        id: 'pages.seminars.form.SeminarForm.duration.placeholder',
        defaultMessage: 'HH:MM',
    },
    nameError: {
        id: 'pages.seminars.AddSeminarDialog.nameError',
        defaultMessage: 'Enter seminar name',
    },
    dateError: {
        id: 'pages.seminars.AddSeminarDialog.dateError',
        defaultMessage: 'Invalid date',
    },
    invalidTimeError: {
        id: 'pages.seminars.AddSeminarDialog.invalidTimeError',
        defaultMessage: 'Invalid time',
    },
    invalidDurationError: {
        id: 'pages.seminars.AddSeminarDialog.invalidDurationError',
        defaultMessage: 'Invalid duration',
    },
    typeError: {
        id: 'pages.seminars.AddSeminarDialog.typeError',
        defaultMessage: 'Select type',
    },
    placeCountError: {
        id: 'pages.seminars.AddSeminarDialog.placeCountError',
        defaultMessage: 'Invalid number',
    },
    minPlaceCountError: {
        id: 'pages.seminars.AddSeminarDialog.minPlaceCountError',
        defaultMessage: 'Place count should not be less than people already attended to seminar',
    },
    bannerError: {
        id: 'pages.seminars.AddSeminarDialog.bannerError',
        defaultMessage: 'Select file',
    },
});

function SeminarForm({ intl, createMode, seminar }) {
    const { control, watch, formState: { errors } } = useFormContext();
    const startAt = watch('startAt');

    const validateEventTime = value => {
        if (!value) return intl.formatMessage(intlMessages.invalidTimeError);
        if (startAt && !isValid(parse(
            `${format(new Date(startAt), 'yyyy-MM-dd')} ${value}`,
            'yyyy-MM-dd HH:mm',
            new Date(),
        ))) {
            return intl.formatMessage(intlMessages.invalidTimeError);
        }
        return true;
    };

    const validatePlaceCount = value => {
        if (!value) return intl.formatMessage(intlMessages.placeCountError);
        if (seminar?.visitors?.count && seminar.visitors.count > value) {
            return intl.formatMessage(intlMessages.minPlaceCountError);
        }
        return true;
    };

    return (
        <Form error={!!errors.root}>
            <Form.Field error={!!errors.name}>
                <Label>
                    <FormattedMessage id="pages.seminars.form.SeminarForm.name.label" defaultMessage="Name" />
                </Label>
                <Controller
                    name="name"
                    control={control}
                    rules={{ required: intl.formatMessage(intlMessages.nameError) }}
                    render={({ field }) => <Input {...field} fluid />}
                />
                {errors.name && <BaseLabel color="red" pointing basic>{errors.name.message}</BaseLabel>}
            </Form.Field>
            <Form.Field>
                <Label>
                    <FormattedMessage id="pages.seminars.form.SeminarForm.description.label" defaultMessage="Description" />
                </Label>
                <Controller
                    name="description"
                    control={control}
                    render={({ field }) => <TextArea {...field} />}
                />
            </Form.Field>
            <Form.Group widths="equal">
                <Form.Field error={!!errors.startAt}>
                    <Label>
                        <FormattedMessage id="pages.seminars.form.SeminarForm.startAt.label" defaultMessage="Date" />
                    </Label>
                    <Controller
                        name="startAt"
                        control={control}
                        rules={{ required: intl.formatMessage(intlMessages.dateError) }}
                        render={({ field: { value, onChange } }) => (
                            <Calendar
                                value={value}
                                onChange={onChange}
                                format="dd.MM.yyyy"
                                autoComplete="off"
                                placeholder={intl.formatMessage(intlMessages.datePlaceholder)}
                            />
                        )}
                    />
                    {errors.startAt && <BaseLabel color="red" pointing basic>{errors.startAt.message}</BaseLabel>}
                </Form.Field>
                <Form.Field error={!!errors.eventTime}>
                    <Label>
                        <FormattedMessage id="pages.seminars.form.SeminarForm.eventTime.label" defaultMessage="Time" />
                    </Label>
                    <Controller
                        name="eventTime"
                        control={control}
                        rules={{ validate: validateEventTime }}
                        render={({ field: { ref: _ref, value, onChange } }) => (
                            <SeminarTime
                                value={value}
                                onChange={(e, data) => onChange(data?.value ?? e)}
                                placeholder={intl.formatMessage(intlMessages.timePlaceholder)}
                            />
                        )}
                    />
                    {errors.eventTime && <BaseLabel color="red" pointing basic>{errors.eventTime.message}</BaseLabel>}
                </Form.Field>
                <Form.Field error={!!errors.duration}>
                    <Label>
                        <FormattedMessage id="pages.seminars.form.SeminarForm.duration.label" defaultMessage="Duration" />
                    </Label>
                    <Controller
                        name="duration"
                        control={control}
                        rules={{ required: intl.formatMessage(intlMessages.invalidDurationError) }}
                        render={({ field: { ref: _ref, value, onChange } }) => (
                            <SeminarDuration
                                value={value}
                                onChange={(e, data) => onChange(data?.value ?? e)}
                                placeholder={intl.formatMessage(intlMessages.durationPlaceholder)}
                            />
                        )}
                    />
                    {errors.duration && <BaseLabel color="red" pointing basic>{errors.duration.message}</BaseLabel>}
                </Form.Field>
            </Form.Group>
            <Form.Group widths="equal">
                <Form.Field error={!!errors.type}>
                    <Label>
                        <FormattedMessage id="pages.seminars.form.SeminarForm.type.label" defaultMessage="Type" />
                    </Label>
                    <Controller
                        name="type"
                        control={control}
                        rules={{ required: intl.formatMessage(intlMessages.typeError) }}
                        render={({ field: { ref: _ref, value, onChange } }) => (
                            <SeminarType
                                value={value}
                                onChange={(e, data) => onChange(data?.value ?? e)}
                            />
                        )}
                    />
                    {errors.type && <BaseLabel color="red" pointing basic>{errors.type.message}</BaseLabel>}
                </Form.Field>
                <Form.Field error={!!errors.placeCount}>
                    <Label>
                        <FormattedMessage id="pages.seminars.form.SeminarForm.placeCount.label" defaultMessage="Place Count" />
                    </Label>
                    <Controller
                        name="placeCount"
                        control={control}
                        rules={{ validate: validatePlaceCount }}
                        render={({ field }) => <Input {...field} type="number" fluid />}
                    />
                    {errors.placeCount && <BaseLabel color="red" pointing basic>{errors.placeCount.message}</BaseLabel>}
                </Form.Field>
            </Form.Group>
            <Form.Group widths="equal">
                <Form.Field error={!!errors.banner}>
                    <Label>
                        <FormattedMessage id="pages.seminars.form.SeminarForm.banner.label" defaultMessage="Banner" />
                    </Label>
                    <Controller
                        name="banner"
                        control={control}
                        rules={{
                            validate: value =>
                                createMode && (!value || value.length === 0)
                                    ? intl.formatMessage(intlMessages.bannerError)
                                    : true,
                        }}
                        render={({ field: { onChange, name } }) => (
                            <input
                                type="file"
                                accept="image/*,.pdf"
                                name={name}
                                onChange={e => onChange(e.target.files)}
                            />
                        )}
                    />
                    {errors.banner && <BaseLabel color="red" pointing basic>{errors.banner.message}</BaseLabel>}
                </Form.Field>
                {seminar?.banner?.url && (
                    <BannerPreviewLink url={seminar.banner.url} />
                )}
            </Form.Group>
        </Form>
    );
}

export default injectIntl(SeminarForm);
